import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight, BookOpen, ChevronDown, Download, Landmark, Scale, ShieldCheck } from "lucide-react";
import SectionHeading from "@/components/common/SectionHeading";
import AnimatedNumber from "@/components/accounts/AnimatedNumber";
import DonutChart from "@/components/accounts/DonutChart";
import BarList from "@/components/accounts/BarList";
import YearBars from "@/components/accounts/YearBars";
import ComparisonRows from "@/components/accounts/ComparisonRows";
import StickyOutline from "@/components/accounts/StickyOutline";
import { annualAccounts2025_26 as d, annualResultImprovement } from "@/data/annualAccounts2025_26";
import { formatCompact, formatCurrency, formatLakhs, formatPercentage } from "@/lib/formatMoney";
import { useInView } from "@/hooks/useInView";

const improvement = annualResultImprovement(d);

function Reveal({ children, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { threshold: 0.12 });
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      } motion-reduce:opacity-100 motion-reduce:translate-y-0 motion-reduce:transition-none`}
    >
      {children}
    </div>
  );
}

export default function AnnualAccountsPage() {
  const [notesOpen, setNotesOpen] = useState(true);
  const pdfUrl = d.pdfUrl;
  const donations = d.programmeIncome[0];
  const scholarship = d.programmeExpenses.find((x) => x.name === "Scholarship Awards");

  const incomeItems = [
    { name: "Programmes & Functions", amount: d.income.programmes },
    { name: "Interest Received", amount: d.income.interest },
    { name: "Membership Fees", amount: d.income.membership },
  ];

  const spendItems = [
    { name: "Programmes & Functions", amount: d.expenditure.programmes },
    { name: "Administrative Expenses", amount: d.expenditure.administrative },
    { name: "Depreciation", amount: d.expenditure.depreciation },
    { name: "Professional Fees", amount: d.expenditure.professionalFees },
    { name: "Audit Fees", amount: d.expenditure.auditFees },
    { name: "Other Charitable Objects", amount: d.expenditure.otherCharitable },
    { name: "COVID Expenses", amount: d.expenditure.covid },
  ];

  const glanceRows = [
    { label: "Total Income", prior: d.income.prior, current: d.income.total },
    { label: "Total Expenditure", prior: d.expenditure.prior, current: d.expenditure.total },
    { label: "Surplus / (Deficit)", prior: d.result.priorDeficit, current: d.result.surplus, signed: true },
    { label: "Reserve & Other Funds", prior: d.funds.reserveAndOtherPrior, current: d.funds.reserveAndOther },
    { label: "Investments", prior: d.balanceSheet.investmentsPrior, current: d.balanceSheet.investments },
    { label: "Net Current Assets", prior: d.balanceSheet.netCurrentAssetsPrior, current: d.balanceSheet.netCurrentAssets },
    { label: "Bank Balance", prior: d.balanceSheet.bankBalancesPrior, current: d.balanceSheet.bankBalances },
    {
      label: "Income & Expenditure Account",
      prior: d.balanceSheet.incomeExpenditureAccountPrior,
      current: d.balanceSheet.incomeExpenditureAccount,
    },
  ];

  return (
    <div data-testid="annual-accounts-page" className="relative">
      <StickyOutline />

      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="relative bg-maroon text-white overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            background:
              "radial-gradient(circle at 80% 20%, rgba(184,234,244,0.35), transparent 42%), radial-gradient(circle at 10% 90%, rgba(0,175,185,0.25), transparent 40%)",
          }}
        />
        <div className="container-bcs relative py-20 sm:py-28">
          <p className="eyebrow !text-gold-light mb-6">
            The Bombay Catholic Sabha · Audited Annual Accounts
          </p>
          <h1 className="heading-xl !text-white max-w-4xl">
            Annual
            <br />
            Accounts
            <br />
            <span className="italic text-gold-light">{d.yearLabel}</span>
          </h1>
          <p className="mt-6 text-white/80 max-w-2xl text-lg leading-relaxed">
            A transparent view of the Bombay Catholic Sabha&apos;s financial performance, resources and
            programmes for the year ended {d.asAt}.
          </p>

          <div className="mt-14 max-w-xl">
            <p className="text-[11px] uppercase tracking-[0.22em] text-gold-light">Annual surplus</p>
            <p className="mt-2 font-serif text-5xl sm:text-6xl font-semibold tabular-nums">
              <AnimatedNumber value={d.result.surplus} signed />
            </p>
            <p className="mt-3 text-sm text-white/70">
              From a {formatLakhs(d.result.priorDeficit, { signed: true })} deficit in {d.priorYearLabel}
            </p>

            <div className="mt-8">
              <div className="flex justify-between text-[11px] uppercase tracking-[0.16em] text-white/50 mb-2">
                <span>
                  {d.priorYearLabel} {formatLakhs(d.result.priorDeficit, { signed: true })}
                </span>
                <span>
                  {d.yearLabel} {formatLakhs(d.result.surplus, { signed: true })}
                </span>
              </div>
              <div className="h-px bg-white/20 relative">
                <span className="absolute left-1/2 -top-1 h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-white/40" />
                <span className="absolute right-0 -top-1.5 h-3.5 w-3.5 rounded-full bg-gold-light accounts-hero-dot" />
              </div>
            </div>
          </div>

          <a
            href="#overview"
            className="mt-16 inline-flex items-center gap-2 text-sm text-gold-light hover:text-white transition-colors"
          >
            Explore the year <ArrowDown size={16} className="animate-bounce" />
          </a>
        </div>
      </section>

      {/* ── The year in numbers ──────────────────────────── */}
      <section id="overview" className="container-bcs py-20 sm:py-28 scroll-mt-24">
        <Reveal>
          <SectionHeading
            eyebrow="01 · The year"
            title="The year in numbers"
            subtitle="A snapshot of the Sabha's financial position during 2025–26."
          />
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
          <Reveal className="md:col-span-7 card-bcs p-8 sm:p-10">
            <p className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">Total income</p>
            <p className="mt-3 font-serif text-5xl sm:text-6xl font-semibold text-ink tabular-nums">
              <AnimatedNumber value={d.income.total} />
            </p>
            <p className="mt-2 text-sm text-ink-muted" title={formatCurrency(d.income.total)}>
              Year ended {d.asAt}
            </p>
          </Reveal>
          <Reveal className="md:col-span-5 card-bcs p-8 sm:p-10 bg-maroon text-white border-maroon">
            <p className="text-[11px] uppercase tracking-[0.2em] text-gold-light">Annual surplus</p>
            <p className="mt-3 font-serif text-5xl font-semibold tabular-nums">
              <AnimatedNumber value={d.result.surplus} />
            </p>
            <p className="mt-2 text-sm text-white/70">Income over expenditure</p>
          </Reveal>
          <Reveal className="md:col-span-4 card-bcs p-7">
            <p className="text-[11px] uppercase tracking-[0.18em] text-ink-muted">Total expenditure</p>
            <p className="mt-2 font-serif text-3xl font-semibold tabular-nums">
              <AnimatedNumber value={d.expenditure.total} />
            </p>
          </Reveal>
          <Reveal className="md:col-span-4 card-bcs p-7">
            <p className="text-[11px] uppercase tracking-[0.18em] text-ink-muted">Total balance sheet</p>
            <p className="mt-2 font-serif text-3xl font-semibold tabular-nums">
              <AnimatedNumber value={d.balanceSheet.total} />
            </p>
            <p className="mt-1 text-xs text-ink-muted">as at {d.asAt}</p>
          </Reveal>
          <Reveal className="md:col-span-4 card-bcs p-7">
            <p className="text-[11px] uppercase tracking-[0.18em] text-ink-muted">Bank investments</p>
            <p className="mt-2 font-serif text-3xl font-semibold tabular-nums">
              <AnimatedNumber value={d.balanceSheet.investments} />
            </p>
          </Reveal>
          <Reveal className="md:col-span-12 sm:col-span-12 card-bcs p-7 md:flex md:items-end md:justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-ink-muted">Net current assets</p>
              <p className="mt-2 font-serif text-3xl font-semibold tabular-nums">
                <AnimatedNumber value={d.balanceSheet.netCurrentAssets} />
              </p>
            </div>
            <p className="mt-3 md:mt-0 text-sm text-ink-muted max-w-md">
              Liquidity after current liabilities — a measure of resources available to the Sabha&apos;s work.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── Turnaround ───────────────────────────────────── */}
      <section className="bg-cream-alt border-y border-cream-dark">
        <div className="container-bcs py-20 sm:py-28">
          <Reveal>
            <SectionHeading
              eyebrow="The turnaround"
              title="From deficit to surplus"
              subtitle="2025–26 marked a significant improvement in the Sabha's annual financial result."
            />
          </Reveal>
          <Reveal className="mt-14">
            <YearBars
              years={[
                {
                  label: d.priorYearLabel,
                  income: d.income.prior,
                  expenditure: d.expenditure.prior,
                  result: d.result.priorDeficit,
                },
                {
                  label: d.yearLabel,
                  income: d.income.total,
                  expenditure: d.expenditure.total,
                  result: d.result.surplus,
                },
              ]}
            />
          </Reveal>
          <Reveal className="mt-16 card-bcs p-8 sm:p-10">
            <p className="text-[11px] uppercase tracking-[0.2em] text-gold-dark">Calculated year-on-year</p>
            <p className="mt-3 font-serif text-4xl sm:text-5xl font-semibold text-ink tabular-nums">
              <AnimatedNumber value={improvement} signed />
            </p>
            <p className="mt-2 text-sm font-medium uppercase tracking-[0.16em] text-ink-muted">
              Improvement in annual result
            </p>
            <p className="mt-5 text-ink-soft leading-relaxed max-w-2xl">
              The Sabha moved from a {formatLakhs(d.result.priorDeficit, { signed: true })} deficit in {d.priorYearLabel} to a{" "}
              {formatLakhs(d.result.surplus)} surplus in {d.yearLabel}. Income more than doubled, while
              expenditure increased by approximately 10%. This {formatLakhs(improvement, { signed: true })} figure
              is a calculated year-on-year improvement, not an audited line item.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── Income ───────────────────────────────────────── */}
      <section id="income" className="container-bcs py-20 sm:py-28 scroll-mt-24">
        <Reveal>
          <SectionHeading
            eyebrow="02 · Income"
            title="Where did the income come from?"
            subtitle={`Total income for ${d.yearLabel} was ${formatLakhs(d.income.total)}.`}
          />
        </Reveal>
        <Reveal className="mt-14">
          <DonutChart items={incomeItems} total={d.income.total} centerLabel="Total income" />
        </Reveal>
        <p className="mt-8 text-sm text-ink-muted max-w-2xl">
          Programmes &amp; Functions were the Sabha&apos;s primary source of income during the year —{" "}
          {formatPercentage(d.income.programmes, d.income.total)} of the total.
        </p>

        <Reveal className="mt-20">
          <SectionHeading
            title="The engine behind the income"
            subtitle={`Programme & Function income contributed ${formatLakhs(d.income.programmes)} during ${d.yearLabel}.`}
          />
        </Reveal>
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-8">
            <BarList
              items={d.programmeIncome}
              total={d.income.programmes}
              highlightName="Donations"
              caption="Programme and function income by source"
            />
          </Reveal>
          <Reveal className="lg:col-span-4 card-bcs p-7">
            <p className="text-[11px] uppercase tracking-[0.18em] text-ink-muted">Largest contributor</p>
            <p className="mt-3 font-serif text-4xl font-semibold tabular-nums">
              <AnimatedNumber value={donations.amount} />
            </p>
            <p className="mt-2 text-sm font-medium text-ink">Donations</p>
            <p className="mt-4 text-sm text-ink-soft leading-relaxed">
              Donations were the largest contributor to Programme &amp; Function income — approximately{" "}
              {formatPercentage(donations.amount, d.income.programmes, 0)} of that category (calculated).
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── Expenditure ──────────────────────────────────── */}
      <section id="expenditure" className="bg-cream-alt border-y border-cream-dark scroll-mt-24">
        <div className="container-bcs py-20 sm:py-28">
          <Reveal>
            <SectionHeading
              eyebrow="03 · Expenditure"
              title="Where the money was spent"
              subtitle={`Total expenditure for ${d.yearLabel} was ${formatLakhs(d.expenditure.total)}.`}
            />
          </Reveal>
          <Reveal className="mt-12">
            <BarList
              items={spendItems}
              total={d.expenditure.total}
              highlightName="Programmes & Functions"
              caption="Expenditure by audited classification"
            />
          </Reveal>
          <p className="mt-6 text-sm text-ink-muted">
            Programme &amp; Function expenditure was the largest category. Other Charitable Objects and COVID
            Expenses were nil for the year.
          </p>

          <Reveal className="mt-20">
            <SectionHeading
              title="Where programme spending went"
              subtitle="Classifications below follow the audited accounts. Not all programme expenditure is charitable expenditure."
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <Reveal className="lg:col-span-8">
              <BarList
                items={d.programmeExpenses}
                total={d.expenditure.programmes}
                highlightName="Scholarship Awards"
                caption="Programme and community spending"
              />
            </Reveal>
            <Reveal className="lg:col-span-4 card-bcs p-7 bg-maroon-50/40 border-maroon/10">
              <p className="text-[11px] uppercase tracking-[0.18em] text-ink-muted">Featured</p>
              <p className="mt-3 font-serif text-4xl font-semibold tabular-nums">
                <AnimatedNumber value={scholarship.amount} />
              </p>
              <p className="mt-2 text-sm font-medium text-ink">Scholarship Awards</p>
              <p className="mt-4 text-sm text-ink-soft leading-relaxed">
                A dedicated line in programme spending, supporting students through the Sabha&apos;s scholarship
                work.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Financial position ───────────────────────────── */}
      <section id="position" className="container-bcs py-20 sm:py-28 scroll-mt-24">
        <Reveal>
          <SectionHeading
            eyebrow="04 · Financial position"
            title="A stronger financial foundation"
            subtitle={`The balance sheet totalled ${formatCompact(d.balanceSheet.total)} as at ${d.asAt}.`}
          />
        </Reveal>
        <Reveal className="mt-12">
          <p className="font-serif text-5xl sm:text-6xl font-semibold tabular-nums text-ink">
            <AnimatedNumber value={d.balanceSheet.total} />
          </p>
          <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-ink-muted">Total balance sheet</p>
        </Reveal>

        <div className="mt-12 space-y-4">
          <Reveal className="card-bcs p-7 sm:p-9">
            <p className="text-[11px] uppercase tracking-[0.18em] text-ink-muted">Investments</p>
            <p className="mt-2 font-serif text-4xl font-semibold tabular-nums">
              <AnimatedNumber value={d.balanceSheet.investments} />
            </p>
            <p className="mt-1 text-sm text-ink-muted">Fixed deposits with banks</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Reveal className="card-bcs p-7">
              <p className="text-[11px] uppercase tracking-[0.18em] text-ink-muted">Net current assets</p>
              <p className="mt-2 font-serif text-3xl font-semibold tabular-nums">
                <AnimatedNumber value={d.balanceSheet.netCurrentAssets} />
              </p>
            </Reveal>
            <Reveal className="card-bcs p-7">
              <p className="text-[11px] uppercase tracking-[0.18em] text-ink-muted">Fixed assets</p>
              <p className="mt-2 font-serif text-3xl font-semibold tabular-nums">
                <AnimatedNumber value={d.balanceSheet.fixedAssetsNet} />
              </p>
              <p className="mt-1 text-xs text-ink-muted">Stated at cost less depreciation</p>
            </Reveal>
          </div>
        </div>

        <Reveal className="mt-20">
          <h3 className="heading-md">Investments &amp; liquidity</h3>
          <p className="mt-3 text-ink-soft max-w-2xl">The Sabha&apos;s investments comprise fixed deposits with banks.</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              label: "Bank investments",
              prior: d.balanceSheet.investmentsPrior,
              current: d.balanceSheet.investments,
            },
            {
              label: "Bank balances",
              prior: d.balanceSheet.bankBalancesPrior,
              current: d.balanceSheet.bankBalances,
              note: `Calculated increase of ${formatLakhs(
                d.balanceSheet.bankBalances - d.balanceSheet.bankBalancesPrior,
                { signed: true }
              )}.`,
            },
            {
              label: "Net current assets",
              prior: d.balanceSheet.netCurrentAssetsPrior,
              current: d.balanceSheet.netCurrentAssets,
            },
          ].map((item) => (
            <Reveal key={item.label} className="card-bcs p-7">
              <p className="text-[11px] uppercase tracking-[0.18em] text-ink-muted">{item.label}</p>
              <p className="mt-4 text-xs text-ink-muted">{d.priorYearLabel}</p>
              <p className="font-serif text-xl text-ink-soft tabular-nums">{formatCompact(item.prior)}</p>
              <p className="mt-3 text-xs text-ink-muted">{d.yearLabel}</p>
              <p className="font-serif text-2xl font-semibold text-ink tabular-nums">{formatCompact(item.current)}</p>
              {item.note && <p className="mt-3 text-xs text-ink-muted leading-relaxed">{item.note}</p>}
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── Funds ────────────────────────────────────────── */}
      <section id="funds" className="bg-cream-alt border-y border-cream-dark scroll-mt-24">
        <div className="container-bcs py-20 sm:py-28">
          <Reveal>
            <SectionHeading
              eyebrow="05 · Funds"
              title="Funds with a purpose"
              subtitle={`Reserve and other funds stood at ${formatCompact(d.funds.reserveAndOther)} at year-end, compared with ${formatCompact(d.funds.reserveAndOtherPrior)} in the previous year.`}
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Reveal className="card-bcs p-8">
              <p className="text-[11px] uppercase tracking-[0.18em] text-ink-muted">Reserve &amp; other funds</p>
              <p className="mt-3 font-serif text-4xl font-semibold tabular-nums">
                <AnimatedNumber value={d.funds.reserveAndOther} />
              </p>
            </Reveal>
            <Reveal className="card-bcs p-8 border-maroon/20">
              <p className="text-[11px] uppercase tracking-[0.18em] text-ink-muted">Corpus fund</p>
              <p className="mt-3 font-serif text-4xl font-semibold tabular-nums">
                <AnimatedNumber value={d.funds.corpus} />
              </p>
              <p className="mt-3 text-sm text-ink-muted">
                Shown separately on the Balance Sheet — not included in Reserve &amp; Other Funds.
              </p>
            </Reveal>
          </div>
          <Reveal className="mt-12">
            <BarList items={d.funds.items} total={d.funds.reserveAndOther} caption="Reserve and other funds" />
          </Reveal>

          <Reveal className="mt-20">
            <h3 className="heading-md">Making resources work</h3>
            <p className="mt-3 text-ink-soft max-w-2xl">
              Interest income of {formatLakhs(d.income.interest)} was primarily generated from fixed deposits with
              banks.
            </p>
          </Reveal>
          <Reveal className="mt-8 max-w-2xl">
            <BarList items={d.interestBreakdown} total={d.income.interest} caption="Interest income" />
          </Reveal>
        </div>
      </section>

      {/* ── Audit ────────────────────────────────────────── */}
      <section id="audit" className="container-bcs py-20 sm:py-28 scroll-mt-24">
        <Reveal>
          <p className="eyebrow mb-4">06 · Accountability</p>
          <h2 className="heading-lg max-w-3xl">
            Audited.
            <br />
            Accountable.
            <br />
            Transparent.
          </h2>
        </Reveal>
        <Reveal className="mt-10 card-bcs p-8 sm:p-10">
          <div className="flex items-start gap-4">
            <span className="h-12 w-12 rounded-xl bg-maroon-50 text-maroon flex items-center justify-center shrink-0">
              <Scale size={22} />
            </span>
            <div>
              <p className="font-serif text-xl sm:text-2xl font-semibold text-ink leading-snug">
                The accounts for the year ended {d.asAt} were audited by {d.auditors}.
              </p>
              <p className="mt-5 text-ink-soft leading-relaxed">
                The Auditor&apos;s Report states that the necessary information and explanations were obtained,
                proper books of account were maintained to the extent examined, and that the financial statements
                give a true and fair view of the Trust&apos;s financial position and its excess of income over
                expenditure for the year.
              </p>
            </div>
          </div>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: BookOpen, title: "Accrual basis", body: "Accounts prepared on an accrual basis." },
            { icon: Landmark, title: "Fixed assets", body: "Stated at cost less depreciation." },
            { icon: ShieldCheck, title: "Investments", body: "Stated at cost." },
          ].map((item) => (
            <Reveal key={item.title} className="card-bcs p-6">
              <item.icon size={18} className="text-maroon" />
              <p className="mt-4 text-[11px] uppercase tracking-[0.18em] text-ink-muted">{item.title}</p>
              <p className="mt-2 text-sm text-ink-soft">{item.body}</p>
            </Reveal>
          ))}
        </div>
        <p className="mt-8 text-sm text-ink-muted">
          The accounts consolidate figures of the Centre as well as the various Parish Units.
        </p>
      </section>

      {/* ── At a glance ──────────────────────────────────── */}
      <section className="bg-cream-alt border-y border-cream-dark">
        <div className="container-bcs py-20 sm:py-28">
          <Reveal>
            <SectionHeading eyebrow="Summary" title="The year at a glance" />
          </Reveal>
          <Reveal className="mt-12">
            <ComparisonRows rows={glanceRows} />
          </Reveal>
        </div>
      </section>

      {/* ── About these figures ──────────────────────────── */}
      <section className="container-bcs py-16 sm:py-20">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-4 text-left"
          onClick={() => setNotesOpen((v) => !v)}
          aria-expanded={notesOpen}
        >
          <h2 className="heading-md">About these figures</h2>
          <ChevronDown size={22} className={`text-maroon shrink-0 transition-transform ${notesOpen ? "rotate-180" : ""}`} />
        </button>
        {notesOpen && (
          <div className="mt-6 space-y-3 text-sm text-ink-soft leading-relaxed max-w-3xl">
            <p>These accounts reflect consolidated figures of the Centre as well as the various Parish Units.</p>
            <p>Figures have been regrouped or rearranged wherever necessary.</p>
            <p>
              For statutory filings with the Charity Commissioner and Income Tax Authorities, a separate set of
              accounts as prescribed under the Bombay Public Trust Act, 1950 has been computed.
            </p>
          </div>
        )}
      </section>

      {/* ── CTA ──────────────────────────────────────────── */}
      <section className="bg-maroon">
        <div className="container-bcs py-20 text-center">
          <h2 className="heading-lg !text-white max-w-2xl mx-auto">
            Want the
            <br />
            full picture?
          </h2>
          <p className="mt-4 text-white/80 max-w-xl mx-auto leading-relaxed">
            Download the complete audited Annual Accounts for {d.yearLabel}.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noreferrer"
              download="BCS-Annual-Accounts-2025-26.pdf"
              className="btn-secondary"
              data-testid="accounts-pdf-btn"
            >
              Download full annual accounts <Download size={16} />
            </a>
            <Link
              to="/about"
              className="inline-flex items-center justify-center gap-2 border border-white/40 text-white hover:bg-white hover:text-maroon rounded-full px-6 py-3 text-sm font-medium transition-colors"
            >
              About the Sabha
            </Link>
          </div>
          <p className="mt-12 font-serif text-xl italic text-gold-light">{d.motto}</p>
        </div>
      </section>
    </div>
  );
}
